import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useResearchDocuments } from "@/hooks/useResearchLibrary";
import { ResearchFilterSidebar, type ResearchFilters } from "@/components/research/ResearchFilterSidebar";
import { ResearchDocumentTable } from "@/components/research/ResearchDocumentTable";
import { ResearchDocViewer } from "@/components/research/ResearchDocViewer";

const DEFAULT_FILTERS: ResearchFilters = { search: "", category: [], author: [] };

export default function ResearchLibrary() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ResearchFilters>(DEFAULT_FILTERS);

  const { data: documents = [], isLoading } = useResearchDocuments();

  const filtered = useMemo(() => {
    return documents.filter((doc) => {
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const match =
          doc.title.toLowerCase().includes(q) ||
          doc.author?.toLowerCase().includes(q) ||
          doc.summary?.toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filters.category.length > 0 && !filters.category.includes(doc.category)) return false;
      if (filters.author.length > 0 && !filters.author.includes(doc.author)) return false;
      return true;
    });
  }, [documents, filters]);

  const authorOptions = useMemo(() => {
    const authors = new Set(documents.map((d) => d.author).filter(Boolean));
    return Array.from(authors).sort();
  }, [documents]);

  if (slug) {
    return (
      <div className="h-[calc(100vh-3rem)]">
        <ResearchDocViewer slug={slug} onBack={() => navigate("/research-library")} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <h1 className="text-3xl font-bold text-foreground">Research Library</h1>
        <p className="text-sm text-muted-foreground">
          Deep research, domain reports, and strategic frameworks
        </p>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-72 shrink-0 border-r overflow-y-auto p-4">
          <ResearchFilterSidebar
            filters={filters}
            onFiltersChange={setFilters}
            totalCount={filtered.length}
            authorOptions={authorOptions}
          />
        </div>

        <div className="flex-1 overflow-auto">
          <ResearchDocumentTable documents={filtered} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
}
